// frontend/src/components/BrandBootLoader.js
//
// The signed-in app's loader (backlog #158): the authenticated-boot splash of
// public/index.html, rebuilt as a React component.
//
// WHY IT LOOKS EXACTLY LIKE THE SPLASH. On an installed PWA's cold start the
// inline splash covers #root while React boots, the session is checked and the
// landing page's chunk downloads (utils/authBoot.js). When it comes down before
// the page is ready - the 3s backstop on a slow phone or network, or a redirect
// - it fades onto whatever #root holds. That used to be two turquoise spinners
// in a row (ProtectedRoute's auth gate, then App's grey PageLoadingFallback).
// ProtectedRoute now renders THIS component in both places instead, so the
// splash crossfades onto an identical picture: same wash, same glow, same
// wordmark at the same size and the same spot. Nothing visibly changes until
// the page itself paints.
//
// TWIN OF public/index.html. The wash, glow and wordmark geometry below are a
// literal copy of the `.lf-auth-boot body::before` / `body::after` /
// `.lf-auth-boot-mark` rules there (no theme token holds these values). Change
// one, change the other - the BrandBootLoader test pins the shared numbers
// against the file.
//
// Only inside the logged-in app: public pages keep PageLoadingFallback (owner
// decision). Kept EAGER (ProtectedRoute imports it statically) because it is
// the fallback FOR lazy chunks - it must never be one itself. It costs no
// request beyond the wordmark SVG the splash already fetched, and imports only
// what the main chunk already holds.

import React, { useState } from "react";
import { Box } from "@mui/material";
import { keyframes } from "@emotion/react";
import Logo from "./brand/Logo";
import useSiteIsHebrew from "../hooks/useSiteIsHebrew";
import { isAuthBootActive } from "../utils/authBoot";
import { VISUALLY_HIDDEN_SX } from "../styles/visuallyHidden";

/** The visually-hidden status label - the component's only text. */
export const BRAND_BOOT_LABEL = Object.freeze({ he: "טוען", en: "Loading" });

// --- Twins of public/index.html ------------------------------------------------

/** `.lf-auth-boot body::before` background-color. */
export const WASH_BASE = "#f8fdff";
/** `.lf-auth-boot body::before` background-image: a mint tint at the top edge over the mint-blush diagonal. */
export const WASH_RADIAL = "radial-gradient(110% 70% at 50% -8%, rgba(211, 240, 234, 0.9), transparent 58%)";
export const WASH_LINEAR = "linear-gradient(150deg, #edfaf8 0%, #f8fdff 45%, #fdf3f7 100%)";
const WASH_IMAGE = `${WASH_RADIAL}, ${WASH_LINEAR}`;

/** Wordmark box: the SVG's 659.15 x 150.32 viewBox at 38px tall (Logo derives the 167px width). */
export const MARK_HEIGHT = 38;
export const MARK_WIDTH = 167;

/** The glow: 150 x 16, its top edge 34px below the viewport's centre. */
export const GLOW_WIDTH = 150;
export const GLOW_HEIGHT = 16;
export const GLOW_OFFSET_Y = 34;
/** `.lf-auth-boot body::after` background: a pool of the brand "AI" teal (#4D7179). */
export const GLOW_GRADIENT = "radial-gradient(closest-side, rgba(77, 113, 121, 0.4), rgba(77, 113, 121, 0) 100%)";

/** The splash's breath keyframes' name - read back to match its phase. */
export const SPLASH_BREATH_NAME = "lf-auth-boot-breath";
/** One breath (1.8s, `alternate`), so a full cycle is two of them. */
export const BREATH_MS = 1800;
const BREATH_CYCLE_MS = BREATH_MS * 2;

const breath = keyframes`
  from {
    transform: scaleX(0.72);
    opacity: 0.5;
  }
  to {
    transform: scaleX(1.05);
    opacity: 1;
  }
`;

// --- Breath phase ---------------------------------------------------------------
//
// The splash's glow has been breathing since the head script ran. A loader that
// started its own breath at mount would crossfade a wide glow into a narrow one
// (two pools of light for 200ms). Instead every loader instance runs on ONE
// clock: the splash's, when it can be read, else navigation start. A negative
// animation-delay of "time since that origin" puts the breath mid-stride, so
// the splash -> loader crossfade and a loader -> loader swap (the auth gate's,
// then the route's Suspense fallback) both keep the same phase.

/** performance.now() at which the shared breath began; resolved once. */
let breathOrigin = null;

/** The splash breath's currentTime in ms, or null when it is not running or cannot be read. */
function readSplashBreathTime() {
  if (!isAuthBootActive()) return null;
  try {
    if (typeof document.getAnimations !== "function") return null;
    const anim = document.getAnimations().find((a) => a && a.animationName === SPLASH_BREATH_NAME);
    const t = anim ? Number(anim.currentTime) : NaN;
    return Number.isFinite(t) ? t : null;
  } catch (e) {
    return null;
  }
}

/**
 * The animation-delay (ms, <= 0) that puts a newly mounted glow in step with
 * the shared breath. Never throws; 0 (start from the beginning) wherever the
 * clock cannot be read.
 *
 * @returns {number}
 */
export function breathDelayMs() {
  try {
    if (typeof performance === "undefined" || typeof performance.now !== "function") return 0;
    const now = performance.now();
    if (!Number.isFinite(now)) return 0;
    if (breathOrigin === null) {
      const splashTime = readSplashBreathTime();
      breathOrigin = splashTime === null ? 0 : now - splashTime;
    }
    const elapsed = Math.max(0, now - breathOrigin);
    // `|| 0`: never -0, which would read back as "-0ms".
    return -Math.round(elapsed % BREATH_CYCLE_MS) || 0;
  } catch (e) {
    return 0;
  }
}

// --- Styles ----------------------------------------------------------------------

/**
 * In the page flow (not position:fixed like the splash) so app-level overlays
 * mounted beside the routes - snackbars, the install prompt - stay above it.
 * CssBaseline zeroes the body margin, so at scroll 0 this box IS the viewport
 * and the absolute children below land where the splash's fixed ones do.
 * 100dvh where supported (the viewport as the splash's four zero offsets see
 * it, browser chrome included); 100vh before that.
 */
const ROOT_SX = {
  position: "relative",
  width: "100%",
  minHeight: "100vh",
  "@supports (height: 100dvh)": { minHeight: "100dvh" },
  overflow: "hidden",
  backgroundColor: WASH_BASE,
  backgroundImage: WASH_IMAGE,
};

const GLOW_BASE_SX = {
  position: "absolute",
  top: `calc(50% + ${GLOW_OFFSET_Y}px)`,
  left: "50%",
  width: `${GLOW_WIDTH}px`,
  height: `${GLOW_HEIGHT}px`,
  // Centred with a negative margin so the breath owns `transform`, as in the splash.
  marginLeft: `${-GLOW_WIDTH / 2}px`,
  borderRadius: "50%",
  background: GLOW_GRADIENT,
  filter: "blur(9px)",
  pointerEvents: "none",
  animation: `${breath} ${BREATH_MS / 1000}s ease-in-out infinite alternate`,
  // The OS setting, and the site's own "stop animations" switch
  // (motion/motionSignal.js publishes both as html.lf-motion-off). Without the
  // animation the glow rests at full opacity and natural width, as the
  // splash's reduced-motion rule leaves it.
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
  "html.lf-motion-off &": { animation: "none" },
};

/** Centred with negative margins at the SVG's box, exactly as `.lf-auth-boot-mark`. */
const MARK_STYLE = Object.freeze({
  position: "absolute",
  top: "50%",
  left: "50%",
  margin: `${-MARK_HEIGHT / 2}px 0 0 ${-MARK_WIDTH / 2}px`,
  display: "block",
});

/**
 * Full-viewport branded loader for the signed-in app: the mint-blush wash, a
 * soft breathing glow and the centred brand wordmark - pixel-twin of the
 * authenticated-boot splash in public/index.html, so the splash can fade onto
 * it without a visible change.
 *
 * Accessibility: a `role="status"` region (polite by definition) holding a
 * visually-hidden "טוען" / "Loading" in the site language; the wordmark is
 * decorative. No `aria-busy`: assistive tech holds back a busy region's
 * content, and this region is inserted WITH its label and removed whole when
 * the page arrives, so a busy flag would only ever silence it. The breath
 * stops under `prefers-reduced-motion: reduce` and the site's own "stop
 * animations" switch.
 *
 * Direction-neutral (everything is centred), identical at every width, takes
 * no props, renders no other text and never throws.
 */
function BrandBootLoader() {
  const [isHebrew] = useSiteIsHebrew();
  // Read once per mount: the phase is fixed at the moment the glow appears.
  const [delayMs] = useState(breathDelayMs);

  return (
    <Box role="status" sx={ROOT_SX} data-testid="brand-boot-loader">
      <Box
        component="span"
        sx={GLOW_BASE_SX}
        style={{ animationDelay: `${delayMs}ms` }}
        aria-hidden="true"
        data-testid="brand-boot-glow"
      />
      <Logo variant="wordmark" tone="light" height={MARK_HEIGHT} decorative style={MARK_STYLE} />
      <Box component="span" sx={VISUALLY_HIDDEN_SX} lang={isHebrew ? "he" : "en"}>
        {isHebrew ? BRAND_BOOT_LABEL.he : BRAND_BOOT_LABEL.en}
      </Box>
    </Box>
  );
}

export default BrandBootLoader;

/** Test seam: forget the shared breath origin. */
export function __resetBreathOriginForTests() {
  breathOrigin = null;
}
